import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Toast } from '~/common/components';
import { decodeOrNull, FlashMessage } from '~/common/utils';
import { axios } from '~/root';
import { qk } from '~/root/query-keys';
import { echo } from '~/root/services';
import { CustomerUser, LoginResponse, OauthData, StaffUser, loginSchema } from '../domain';

const oauthWindow = {
  width: 450,
  height: 600,
};

type OauthLoginProvider = 'microsoft' | 'google';

export const oauthLoginProviderSchema = z.object({
  channel: z.string(),
  redirectUrl: z.string(),
});

const openOauthWindow = (url: string) => {
  if (!window.top) {
    throw new Error('No window.top');
  }

  const posX = window.top.outerWidth / 2 + window.top.screenX - oauthWindow.width / 2;
  const posY = window.top.outerHeight / 2 + window.top.screenY - oauthWindow.height / 2;

  return window.open(
    url,
    'provider',
    `width=${oauthWindow.width}, height=${oauthWindow.height}, left=${posX}, top=${posY}`,
  );
};

type OauthResponse = LoginResponse | (LoginResponse & { user: CustomerUser | StaffUser });
type OauthAction = 'login' | 'signup';

export const oauthLogin = ({
  provider,
  oauthData,
  news_letter = false,
  action,
}: {
  provider: OauthLoginProvider;
  oauthData?: OauthData | null;
  news_letter?: boolean;
  action: OauthAction;
}) => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<OauthResponse>(async (resolve, reject) => {
    const response: OauthData =
      oauthData ??
      (await axios
        .post(`/v1/oauth/login/${provider}`, { news_letter, action })
        .then((res) => oauthLoginProviderSchema.parse(res.data)));

    const stopListening = () => {
      echo.leaveChannel(response.channel);
    };

    echo.channel(response.channel).listen('.authenticated', (data: unknown) => {
      stopListening();
      const loginResponse = LoginResponse.safeParse(data);
      const user = loginSchema.safeParse(data);
      if (!user.success) {
        captureException(user.error);
      }
      if (loginResponse.success && user.success) {
        resolve({ ...loginResponse.data, user: user.data });
      }
      if (loginResponse.success) {
        resolve(loginResponse.data);
      }
    });

    echo.channel(response.channel).listen('.notification', (data: unknown) => {
      stopListening();
      const flash = decodeOrNull(data, FlashMessage);
      if (flash) {
        Toast[flash.type]({ title: flash.message });
      }
      reject(data);
    });

    const oauthWindow = openOauthWindow(response.redirectUrl);

    const check = setInterval(() => {
      if (oauthWindow?.closed) {
        clearInterval(check);
        // it's possible that auth ws message could arrive after oauth window
        // was closed, so let's wait for that case
        setTimeout(() => {
          stopListening();
          reject(new Error('oauth window was closed before auth attempt'));
        }, 5_000);
      }
    }, 500);
  });
};

export const useOauthLogin = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      provider,
      oauthData,
      news_letter,
      action,
    }: {
      provider: OauthLoginProvider;
      oauthData?: OauthData | null;
      news_letter?: boolean;
      action: OauthAction;
    }) => {
      return oauthLogin({ provider, oauthData, news_letter, action })
        .then((response) => {
          if (new URL(response.redirectUrl).origin !== window.location.origin) {
            window.location.href = response.redirectUrl;
            return response;
          }
          client.invalidateQueries(qk.init);

          return response;
        })
        .catch((error) => {
          console.error(error);
          captureException(error);
          return null;
        });
    },
  });
};
